import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { usePlatformBanner } from '../../hooks/usePlatformBanner';

const {
  FiX, FiAlertTriangle, FiInfo, FiAlertCircle,
  FiCheckCircle, FiTool, FiGift, FiStar,
  FiZap, FiClock, FiCalendar,
} = FiIcons;

/**
 * Maps theme IDs to react-icons fallback icons.
 * Promotional / Seasonal themes fall back to the emoji `icon` field
 * from the theme object (rendered as text), so only System themes
 * need an explicit react-icon mapping.
 */
const REACT_ICON_MAP = {
  warning:     FiAlertTriangle,
  info:        FiInfo,
  danger:      FiAlertCircle,
  success:     FiCheckCircle,
  maintenance: FiTool,
  promotional: FiGift,
  'promotional/sale':        FiZap,
  'promotional/new-feature': FiStar,
  'promotional/referral':    FiGift,
  'promotional/anniversary': FiStar,
  custom:      FiAlertTriangle,
};

/** Dismiss key is scoped to the exact banner content so re-edits re-show it */
function buildDismissKey(config) {
  return `em_banner_dismissed_v:${config.bannerTheme}:${config.bannerText?.slice(0, 30)}`;
}

/** Format seconds into HH:MM:SS or MM:SS */
function formatCountdown(seconds) {
  if (seconds <= 0) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = n => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

const MaintenanceBanner = () => {
  const { config, isActive, resolvedColor, currentTheme } = usePlatformBanner();
  const [dismissed, setDismissed]   = useState(false);
  const [countdown, setCountdown]   = useState(null);

  // ── Reset dismissal when banner identity changes ──────────────────────────
  useEffect(() => {
    const key = buildDismissKey(config);
    setDismissed(sessionStorage.getItem(key) === '1');
  }, [config.bannerText, config.bannerTheme, config.bannerColor, config.platform_maintenance]);

  // ── Live countdown toward endAt ───────────────────────────────────────────
  useEffect(() => {
    if (!isActive || !config.endAt) {
      setCountdown(null);
      return;
    }
    const tick = () => {
      const remaining = Math.floor((new Date(config.endAt).getTime() - Date.now()) / 1000);
      setCountdown(remaining > 0 ? remaining : null);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isActive, config.endAt]);

  const handleDismiss = useCallback(() => {
    sessionStorage.setItem(buildDismissKey(config), '1');
    setDismissed(true);
  }, [config]);

  // ── Visibility gate ───────────────────────────────────────────────────────
  const shouldShow = isActive && !dismissed;

  // ── Icon resolution ───────────────────────────────────────────────────────
  // Prefer a react-icon for system themes; fall back to the emoji from the theme object.
  const ReactIcon      = REACT_ICON_MAP[config.bannerTheme] || null;
  const themeEmoji     = currentTheme?.icon || '📢';
  const useEmojiIcon   = !ReactIcon; // seasonal themes use emoji

  // ── Inline style — always driven by resolvedColor from the hook ───────────
  const bannerStyle = { backgroundColor: resolvedColor };

  // ── Scheduled-but-not-yet-active pill ────────────────────────────────────
  // (shown in admin preview only — shouldShow handles real visibility)
  const isScheduledFuture =
    config.platform_maintenance &&
    config.startAt &&
    new Date(config.startAt).getTime() > Date.now();

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          id="platform-maintenance-banner"
          key={`banner-${config.bannerTheme}-${config.bannerText?.slice(0, 10)}`}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="w-full z-[200] overflow-hidden"
          style={bannerStyle}
          role="alert"
          aria-live="polite"
          aria-label={`Platform banner: ${config.bannerText}`}
        >
          <div className="container mx-auto px-4 py-2.5 flex items-center justify-between gap-3 min-h-[44px]">

            {/* Left: icon + message */}
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              {config.showIcon && (
                <span className="flex-shrink-0 opacity-90 leading-none" aria-hidden="true">
                  {useEmojiIcon ? (
                    <span className="text-base">{themeEmoji}</span>
                  ) : (
                    <SafeIcon icon={ReactIcon} className="w-4 h-4 text-white" />
                  )}
                </span>
              )}

              <p className="text-white text-xs font-semibold leading-snug line-clamp-2 md:line-clamp-1 flex-1 min-w-0">
                {config.bannerText || 'Platform maintenance in progress.'}
              </p>
            </div>

            {/* Centre-right: countdown + schedule pills */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Countdown timer */}
              {countdown !== null && (
                <span className="hidden sm:flex items-center gap-1 text-white/90 text-[10px] font-black
                                 bg-black/20 px-2.5 py-1 rounded-full tracking-widest uppercase whitespace-nowrap">
                  <SafeIcon icon={FiClock} className="w-3 h-3" />
                  {formatCountdown(countdown)}
                </span>
              )}

              {/* Scheduled-in-future pill (admin preview awareness) */}
              {isScheduledFuture && (
                <span className="hidden sm:flex items-center gap-1 text-white/90 text-[10px] font-black
                                 bg-black/20 px-2.5 py-1 rounded-full tracking-widest uppercase whitespace-nowrap">
                  <SafeIcon icon={FiCalendar} className="w-3 h-3" />
                  Scheduled
                </span>
              )}

              {/* Dismiss button */}
              {config.dismissible && (
                <button
                  onClick={handleDismiss}
                  aria-label="Dismiss banner"
                  className="p-1 rounded-full hover:bg-black/20 transition-colors text-white/80
                             hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40"
                >
                  <SafeIcon icon={FiX} className="w-4 h-4" />
                </button>
              )}
            </div>

          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MaintenanceBanner;